import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";

const DeactivateSubscriptionDialog = ({
  open,
  onClose,
  selectedBusinessId,
}) => {
  const { t } = useTranslation('dashboard');
  const [step, setStep] = useState(1); // 1 = confirmation, 2 = success
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();

  // Function to get next month name
  const getNextMonthName = useCallback(() => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
  }, []);

  // Deactivate subscription mutation
  const deactivateMutation = useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.post(
        ApiEndPoints.BUSINESS.cancelSubscription,
        data
      );
      return res.data;
    },
    onSuccess: () => {
      // Move to success step
      setStep(2);
      // Invalidate business location data to refresh subscription status
      queryClient.invalidateQueries(["businessLocationData", selectedBusinessId]);
    },
    onError: (error) => {
      console.error("Failed to deactivate subscription:", error);
      // You can add toast notification here if needed
    },
  });

  const handleConfirm = useCallback(() => {
    deactivateMutation.mutate({
      businessId: selectedBusinessId,
      cancelAtPeriodEnd: true,
      reason: reason || undefined,
    });
  }, [deactivateMutation, selectedBusinessId, reason]);

  const handleClose = useCallback(() => {
    onClose();
    // Reset state when closing
    setStep(1);
    setReason("");
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "#fff",
          p: 2,
        },
      }}
      sx={{
        zIndex: 1400, // Higher than SetupDialog
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          pb: 2,
          pt: 3,
          px: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#6200EE",
            fontWeight: 600,
            textTransform: "lowercase",
            fontSize: 20,
          }}
        >
          {step === 1
            ? t('deactivateSubscription')
            : t('deactivationOfSubscription')}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            bgcolor: "#F3F5F9",
            "&:hover": { bgcolor: "#E0E0E0" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {step === 1 ? (
          // Step 1: Confirmation
          <Box>
            <Typography
              sx={{
                mb: 2,
                fontSize: 15,
                color: "#000",
                lineHeight: 1.6,
              }}
            >
              {t('deactivateConfirmQuestion')}
            </Typography>

            <Typography
              sx={{
                mb: 1.5,
                fontSize: 14,
                color: "#000",
                lineHeight: 1.6,
              }}
            >
              {t('deactivateAtEndOfMonth')}
            </Typography>

            <Typography
              sx={{
                mb: 3,
                fontSize: 14,
                color: "#000",
                lineHeight: 1.6,
              }}
            >
              {t('reactivateWithin24Months')}
            </Typography>

            <Typography
              sx={{
                mb: 1.5,
                fontSize: 14,
                fontWeight: 600,
                color: "#222",
              }}
            >
              {t('reasonForDeactivation')}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={3}
              placeholder={t('enterReasonHere')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              sx={{
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#E0E0E0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#A259FF",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#A259FF",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#999",
                  opacity: 1,
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={handleConfirm}
                size="large"
                disabled={deactivateMutation.isLoading}
                sx={{
                  bgcolor: "#9347FF",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#8938e9" },
                  "&:disabled": {
                    bgcolor: "#A259FF",
                    opacity: 0.6,
                  },
                }}
              >
                {deactivateMutation.isLoading
                  ? t('processing')
                  : t('yesDeactivate')}
              </Button>
              <Button
                variant="contained"
                onClick={handleClose}
                size="large"
                sx={{
                  bgcolor: "#9347FF",
                  color: "#fff",
                  textTransform: "none",
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#8938e9" },
                }}
              >
                {t('goBack')}
              </Button>
            </Box>
          </Box>
        ) : (
          // Step 2: Success
          <Box>
            <Typography
              sx={{
                mb: 2,
                fontSize: 15,
                color: "#222",
                lineHeight: 1.6,
              }}
            >
              {t('youveDeactivatedSubscription')}
            </Typography>

            <Typography
              sx={{
                mb: 4,
                fontSize: 15,
                color: "#222",
                lineHeight: 1.6,
              }}
            >
              {t('accountUnavailableNextMonth', { month: getNextMonthName() })}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  bgcolor: "#A259FF",
                  color: "#fff",
                  textTransform: "lowercase",
                  borderRadius: 2,
                  px: 4,
                  py: 1,
                  fontWeight: 500,
                  "&:hover": { bgcolor: "#8938e9" },
                }}
              >
                {t('ok')}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeactivateSubscriptionDialog;

